// redux/services/employmentService/employment.Services.js
import { baseApi } from "../baseApi";

export const EmploymentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ======================
    // ADMIN APIs
    // ======================

    // CREATE JOB
    createJob: builder.mutation({
      query: (data) => ({
        url: "/admin/job-create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Employment"],
    }),

    // GET ALL JOBS (Admin)
    getAdminJobs: builder.query({
      query: ({
        page = 1,
        limit = 10,
        status = "",
        category = "",
        type = "",
        search = "",
      } = {}) =>
        `/admin/jobs?page=${page}&limit=${limit}&status=${status}&category=${category}&type=${type}&search=${search}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Employment", id })),
              { type: "Employment", id: "LIST" },
            ]
          : [{ type: "Employment", id: "LIST" }],
    }),

    // GET JOB BY ID (Admin)
    getAdminJob: builder.query({
      query: (id) => `/admin/job/${id}`,
      providesTags: (result, error, id) => [{ type: "Employment", id }],
    }),

    // UPDATE JOB
    updateJob: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/job-update/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Employment", id },
        { type: "Employment", id: "LIST" },
      ],
    }),

    // DELETE JOB
    deleteJob: builder.mutation({
      query: (id) => ({
        url: `/admin/job-delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Employment", id },
        { type: "Employment", id: "LIST" },
      ],
    }),

    // UPDATE JOB STATUS
    updateJobStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/job-status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Employment", id },
        { type: "Employment", id: "LIST" },
      ],
    }),

    // GET JOB STATISTICS
    getJobStatistics: builder.query({
      query: () => "/admin/job-statistics",
      providesTags: ["Employment"],
    }),

    // GET JOB APPLICATIONS
    getJobApplications: builder.query({
      query: (job_id) => `/admin/job-applications/${job_id}`,
      providesTags: (result, error, job_id) => [{ type: "Employment", job_id }],
    }),

    // UPDATE APPLICATION STATUS
    updateApplicationStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/application-status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Employment", id },
      ],
    }),

    // ======================
    // FRONTEND APIs (Public)
    // ======================

    // GET ALL JOBS (Frontend)
    getJobs: builder.query({
      query: ({
        page = 1,
        limit = 10,
        category = "",
        type = "",
        city = "",
        state = "",
        search = "",
      } = {}) =>
        `/jobs?page=${page}&limit=${limit}&category=${category}&type=${type}&city=${city}&state=${state}&search=${search}`,
      providesTags: ["Employment"],
    }),

    // GET JOB BY SLUG
    getJobBySlug: builder.query({
      query: (slug) => `/job/${slug}`,
      providesTags: (result, error, slug) => [{ type: "Employment", slug }],
    }),

    // GET JOB CATEGORIES
    getJobCategories: builder.query({
      query: () => "/job-categories",
      providesTags: ["Employment"],
    }),

    // GET JOB TYPES
    getJobTypes: builder.query({
      query: () => "/job-types",
      providesTags: ["Employment"],
    }),

    // GET FEATURED JOBS
    getFeaturedJobs: builder.query({
      query: ({ limit = 6 } = {}) => `/jobs-featured?limit=${limit}`,
      providesTags: ["Employment"],
    }),

    // GET RECENT JOBS
    getRecentJobs: builder.query({
      query: ({ limit = 5 } = {}) => `/jobs-recent?limit=${limit}`,
      providesTags: ["Employment"],
    }),

    // APPLY FOR JOB
    applyForJob: builder.mutation({
      query: (data) => ({
        url: "/job-apply",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Employment"],
    }),
  }),
});

export const {
  // Admin Mutations
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
  useUpdateJobStatusMutation,
  useUpdateApplicationStatusMutation,

  // Admin Queries
  useGetAdminJobsQuery,
  useGetAdminJobQuery,
  useGetJobStatisticsQuery,
  useGetJobApplicationsQuery,

  // Frontend Queries
  useGetJobsQuery,
  useGetJobBySlugQuery,
  useGetJobCategoriesQuery,
  useGetJobTypesQuery,
  useGetFeaturedJobsQuery,
  useGetRecentJobsQuery,

  // Frontend Mutations
  useApplyForJobMutation,
} = EmploymentApi;

export default EmploymentApi;